import { Wallet, Search, ListFilter, RotateCcw, Filter, X, Calendar } from "lucide-react";
import TopUpModal from "./components/TopUpModal";
import { useRef, useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { getAllRechargeLogs } from "../../../services/product_management_service.js";
import { handleSetSearch } from "../../../utils/searchDebouncing.js";
import ARHCustomDataTable from "../../../components/dataTable/ARHCustomDataTable.jsx";
import RechargeReceipt from "./components/RechargeReciept.jsx";
import agentRollbackService from "../../../services/agentRollbackService.js";
import { toast } from "react-toastify";
import ARHAdvanceFilterModal from "../../../components/application/date-picker/FilterModal";
import { ARHCustomDateFiler } from "../../../components/application/date-picker/rangeCallernderCard";
import { useTranslation } from "react-i18next";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StatusBadge = ({ status, t }) => {
  const statusMap = {
    success: {
      variant: "light",
      color: "success",
      label: t('recharge3.status.success')
    },
    failed: {
      variant: "light",
      color: "error",
      label: t('recharge3.status.failed')
    },
    pending: {
      variant: "light",
      color: "warning",
      label: t('recharge3.status.pending')
    },
    processing: {
      variant: "light",
      color: "info",
      label: t('recharge3.status.processing')
    }
  };

  const badgeProps = statusMap[status?.toLowerCase()] || {
    variant: "light",
    color: "light",
    label: status || t('recharge3.status.unknown')
  };

  return (
    <div className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center justify-center ${
      status?.toLowerCase() === "success"
        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
        : status?.toLowerCase() === "failed"
        ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
        : status?.toLowerCase() === "pending"
        ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
        : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
    }`}>
      {badgeProps.label}
    </div>
  );
};

// Rollback Status Badge
const RollbackStatusBadge = ({ isRollback, t }) => {
  return (
    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
      isRollback 
        ? "bg-secondary-100 text-secondary-700 dark:bg-secondary-900/30 dark:text-secondary-300" 
        : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
    }`}>
      {isRollback ? t('recharge3.rollbackStatus.requested') : t('recharge3.rollbackStatus.none')}
    </div>
  );
};

function Recharge() {
  const { t } = useTranslation();
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const searchRef = useRef(null);
  const userInfo = useSelector((state) => state.auth.user);
  const [refetch, setRefetch] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [selectedRechargeForRollback, setSelectedRechargeForRollback] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const queryClient = useQueryClient();

  // Dark mode detection
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
    agentId: userInfo?.uid,
    'createdAt[gte]': null,
    'createdAt[lte]': null,
    status: null,
    operator: null,
    company: null,
    'amount[gte]': null,
    'amount[lte]': null,
    isRollback: null,
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["rechargeLogs", filter, refetch],
    queryFn: () => getAllRechargeLogs(userInfo?.uid, filter),
  });

  const rollbackMutation = useMutation({
    mutationFn: (rechargeLogId) => agentRollbackService.markRechargeAsRollback(rechargeLogId),
    onSuccess: () => {
      toast.success(t('recharge3.toasts.rollbackSuccess'));
      setRollbackModalOpen(false);
      setSelectedRechargeForRollback(null);
      setRefetch((prev) => !prev);
      
      queryClient.invalidateQueries({
        queryKey: ["agentRollbacks"],
        exact: false
      });
    },
    onError: (error) => {
      console.error("Rollback error:", error);
      toast.error(error?.response?.data?.error || t('recharge3.toasts.rollbackFailed'));
    },
  });

  const handleRefetch = () => setRefetch((prev) => !prev);
  const closeTopupModal = () => {
    setTopupModalOpen(false);
  };

  const handleTopUpSuccess = () => {
    setTopupModalOpen(false); 
    setRefetch((prev) => !prev);
  };

  const handleSearchChange = (value) => {
    setFilter((prev) => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setFilter((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleRollbackRequest = (rechargeLog) => {
    if (rechargeLog.status !== "success") {
      toast.error(t('recharge3.toasts.onlySuccessRollback'));
      return;
    }
    
    if (rechargeLog.isRollback) {
      toast.info(t('recharge3.toasts.alreadyRollback'));
      return;
    }
    
    setSelectedRechargeForRollback(rechargeLog);
    setRollbackModalOpen(true);
  };

  const confirmRollback = () => {
    if (selectedRechargeForRollback) {
      rollbackMutation.mutate(selectedRechargeForRollback.id);
    }
  };

  const handleDateRangeApply = (startDate, endDate) => {
    setFilter(prev => ({
      ...prev,
      'createdAt[gte]': startDate,
      'createdAt[lte]': endDate,
      page: 1,
    }));
    setIsDatePickerOpen(false);
  };

  const clearDateFilter = () => {
    setFilter(prev => {
      const newFilter = { ...prev };
      delete newFilter['createdAt[gte]'];
      delete newFilter['createdAt[lte]'];
      return { ...newFilter, page: 1 };
    });
  };

  const formatDateRange = () => {
    if (!filter['createdAt[gte]'] || !filter['createdAt[lte]']) return null;
    const start = new Date(filter['createdAt[gte]']);
    const end = new Date(filter['createdAt[lte]']);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const filterConfig = useMemo(() => [
    {
      key: 'status',
      label: t('recharge3.filters.status'),
      type: 'dropdown',
      options: [
        { value: 'success', label: t('recharge3.status.success') },
        { value: 'failed', label: t('recharge3.status.failed') },
        { value: 'pending', label: t('recharge3.status.pending') },
        { value: 'processing', label: t('recharge3.status.processing') },
      ],
      multiSelect: false,
    },
    {
      key: 'isRollback',
      label: t('recharge3.filters.rollbackStatus'),
      type: 'dropdown',
      options: [
        { value: 'true', label: t('recharge3.rollbackStatus.requested') },
        { value: 'false', label: t('recharge3.rollbackStatus.none') },
      ],
      multiSelect: false,
    },
    {
      key: 'amount',
      label: t('recharge3.filters.amountRange'),
      type: 'range',
      min: 0,
      max: 50000,
      unit: 'AFN',
    }
  ], [t]);

  const initialAdvanceValues = useMemo(() => {
    return {
      status: filter.status || '',
      operator: filter.operator || '',
      isRollback: filter.isRollback || '',
      amount: {
        min: filter['amount[gte]'] || 0,
        max: filter['amount[lte]'] || 50000,
      }
    };
  }, [filter]);

  const handleApplyAdvanceFilters = (filterValues) => {
    setFilter(prev => {
      const newFilter = { ...prev, page: 1 };
      
      if (filterValues.status) {
        newFilter.status = filterValues.status;
      } else {
        delete newFilter.status;
      }

      if (filterValues.operator) {
        newFilter.operator = filterValues.operator;
      } else {
        delete newFilter.operator;
      }
      
      if (filterValues.isRollback) {
        newFilter.isRollback = filterValues.isRollback === 'true';
      } else {
        delete newFilter.isRollback;
      }
      
      if (filterValues.amount) {
        if (filterValues.amount.min > 0) {
          newFilter['amount[gte]'] = filterValues.amount.min;
        } else {
          delete newFilter['amount[gte]'];
        }
        
        if (filterValues.amount.max < 50000) {
          newFilter['amount[lte]'] = filterValues.amount.max;
        } else {
          delete newFilter['amount[lte]'];
        }
      } else {
        delete newFilter['amount[gte]'];
        delete newFilter['amount[lte]'];
      }
      
      return newFilter;
    });
    
    setIsAdvanceFilterOpen(false);
  };

  const clearAllFilters = () => {
    setFilter({
      search: "",
      limit: 10,
      page: 1,
      agentId: userInfo?.uid,
      'createdAt[gte]': null,
      'createdAt[lte]': null,
      status: null,
      operator: null,
      company: null,
      'amount[gte]': null,
      'amount[lte]': null,
      isRollback: null,
    });
  };

  const isAnyFilterApplied = useMemo(() => {
    return (
      filter.search || 
      filter['createdAt[gte]'] || 
      filter['createdAt[lte]'] || 
      filter.status || 
      filter.operator || 
      filter.company ||
      filter['amount[gte]'] || 
      filter['amount[lte]'] ||
      filter.isRollback !== null
    );
  }, [filter]);

  const columns = [
    { 
      key: "txnNumber", 
      label: t('recharge3.columns.transactionId'),
      render: (item) => (
        <span className={`font-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-800'}`}>
          {item.txnNumber}
        </span>
      )
    },
    { 
      key: "receiver", 
      label: t('recharge3.columns.receiver'),
      render: (item) => (
        <div>
          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {item.receiver}
          </div>
          {item.receiverType && (
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {item.receiverType}
            </div>
          )}
        </div>
      )
    },
    { 
      key: "amount", 
      label: t('recharge3.columns.amount'),
      render: (item) => (
        <span className={`font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
          {parseFloat(item?.amount).toFixed(2) || 0} AFN
        </span>
      )
    },
    
    {
      key: "status",
      label: t('recharge3.columns.status'),
      render: (item) => <StatusBadge status={item?.status} t={t} />
    },
    {
      key: "rollbackStatus",
      label: t('recharge3.columns.rollbackStatus'),
      render: (item) => <RollbackStatusBadge isRollback={item?.isRollback} t={t} />
    },
    {
      key: "createdAt",
      label: t('recharge3.columns.createdAt'),
      render: (item) => (
        <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
          {new Date(item?.createdAt).toLocaleString(t('common.locale') || 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      )
    },
    {
      key: "actions",
      label: t('recharge3.columns.actions'),
      render: (item) => (
        <div className="flex gap-2">
          <button 
            onClick={() => {
              setSelectedTransaction(item);
              setReceiptOpen(true);
            }}
            className={`text-secondary-600 hover:text-secondary-800 text-sm px-3 py-1 border border-secondary-600 rounded-md hover:bg-secondary-50 transition-colors ${
              isDark ? 'dark:text-secondary-400 dark:border-secondary-400 dark:hover:bg-secondary-900/30' : ''
            }`}
          >
            {t('recharge3.actions.receipt')}
          </button>
          
          {item.status === "success" && !item.isRollback && (
            <button 
              onClick={() => handleRollbackRequest(item)}
              className={`text-orange-600 hover:text-orange-800 text-sm px-3 py-1 border border-orange-600 rounded-md hover:bg-orange-50 transition-colors flex items-center gap-1 ${
                isDark ? 'dark:text-orange-400 dark:border-orange-400 dark:hover:bg-orange-900/30' : ''
              }`}
            >
              <RotateCcw size={14} />
              {t('recharge3.actions.rollback')}
            </button>
          )}
          
          {item.isRollback && (
            <span className={`text-gray-500 text-sm px-3 py-1 border border-gray-400 rounded-md bg-gray-50 ${
              isDark ? 'dark:text-gray-400 dark:border-gray-600 dark:bg-gray-800' : ''
            }`}>
              {t('recharge3.actions.rollbackPending')}
            </span>
          )}
        </div>
      )
    }
  ];

  const customEmptyState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center p-4">
      <img 
        src="/images/Search.png" 
        alt={t('recharge3.emptyState.alt')} 
        className="w-[170px] h-[170px]"
      />
      <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {filter.search 
          ? t('recharge3.emptyState.noResults', { search: filter.search })
          : t('recharge3.emptyState.noRecords')}
      </p>
      {isAnyFilterApplied && (
        <button
          onClick={clearAllFilters}
          className="mt-3 text-sm text-red-600 hover:text-red-700 underline dark:text-red-400 dark:hover:text-red-300"
        >
          {t('recharge3.emptyState.clearFilters')}
        </button>
      )}
    </div>
  );

  const customErrorState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center p-4">
      <img 
        src="/images/Search.png" 
        alt={t('recharge3.errorState.alt')} 
        className="w-[170px] h-[170px]"
      />
      <div className="text-sm text-red-600 dark:text-red-400 mb-2">
        {error?.message || t('recharge3.errorState.message')}
      </div>
      <button
        onClick={handleRefetch}
        className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
      >
        {t('recharge3.errorState.retry')}
      </button>
    </div>
  );

  return (
    <div className="w-full px-5 py-4 min-h-screen h-auto bg-[linear-gradient(135deg,_#EFF2FF_0%,_#FAF5FF_50%,_#FCF3FB_100%)] dark:bg-[linear-gradient(135deg,_#1e293b_0%,_#334155_50%,_#0f172a_100%)]">
      <TopUpModal open={topupModalOpen} onClose={closeTopupModal} onSuccess={handleTopUpSuccess} />
      <RechargeReceipt
        transaction={selectedTransaction} 
        isOpen={receiptOpen} 
        onClose={() => setReceiptOpen(false)} 
      />
      
      {rollbackModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 w-96 max-w-[90%] ${
            isDark ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('recharge3.rollbackModal.title')}
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('recharge3.rollbackModal.confirmMessage')}
            </p>
            {selectedRechargeForRollback && (
              <div className={`p-3 rounded mb-4 ${
                isDark ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className="flex justify-between mb-2">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {t('recharge3.rollbackModal.receiver')}:
                  </span>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {selectedRechargeForRollback.receiver}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {t('recharge3.rollbackModal.amount')}:
                  </span>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {parseFloat(selectedRechargeForRollback.amount).toFixed(2)} AFN
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {t('recharge3.rollbackModal.transactionId')}:
                  </span>
                  <span className={`font-mono text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {selectedRechargeForRollback.txnNumber}
                  </span>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRollbackModalOpen(false)}
                className={`px-4 py-2 border rounded-md transition-colors ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmRollback}
                disabled={rollbackMutation.isPending}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
              >
                {rollbackMutation.isPending ? t('common.processing') : t('recharge3.rollbackModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-5 mt-5">
        <div className="md:w-[80%] flex gap-3 flex-wrap">
          <div className="w-full flex gap-2 relative">
            <div className={`relative text-[14px] bg-gradient-to-r md:w-[600px] from-[#F8F4FF] to-[#FAF9F3] rounded-md border ${
              isDark 
                ? 'dark:from-slate-800 dark:to-slate-800 dark:border-gray-500' 
                : 'border-[#E4E7EC]'
            }`}>
              <input
                onChange={(e) => {
                  handleSetSearch(handleSearchChange, e.target.value, searchRef);
                }}
                type="text"
                placeholder={t('recharge3.search.placeholder')}
                defaultValue={filter.search}
                className={`bg-transparent h-[44px] pl-10 pr-8 transition-all duration-300 w-full outline-none px-3 py-2 ${
                  isDark ? 'dark:text-white dark:placeholder-gray-400' : ''
                }`}
              />
              <Search size={18} className={`absolute top-[12px] left-[8px] ${
                isDark ? 'text-gray-400' : 'text-[#667085]'
              }`} />
              {filter.search && (
                <button
                  onClick={() => handleSearchChange("")}
                  className={`absolute top-[12px] right-[8px] hover:text-red-500 ${
                    isDark ? 'text-gray-400' : 'text-[#667085]'
                  }`}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <div className="flex w-full gap-2">
              <button
                onClick={() => setIsDatePickerOpen(true)}
                className={`max-w-[250px] cursor-pointer w-full md:w-auto border rounded-md px-3 py-2 h-[44px] flex items-center justify-center gap-2 bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] relative ${
                  isDark 
                    ? 'dark:border-gray-500 dark:from-slate-800 dark:to-slate-800' 
                    : 'border-[#E4E7EC]'
                }`}
              >
                <Calendar size={20} className={isDark ? 'text-gray-400' : 'text-[#667085]'} />
                <p className={`text-[14px] truncate ${isDark ? 'text-gray-300' : 'text-[#667085]'}`}>
                  {filter['createdAt[gte]'] && filter['createdAt[lte]'] 
                    ? <span className="pr-5">{formatDateRange()}</span> 
                    : t('recharge3.filters.dateRange')}
                </p>
                
                {filter['createdAt[gte]'] && filter['createdAt[lte]'] && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      clearDateFilter();
                    }}
                    className="absolute right-2 text-secondary-800 dark:text-secondary-200 hover:text-red-500"
                  >
                    <X size={16} />
                  </button>
                )}
              </button>
              <button
                onClick={() => setIsAdvanceFilterOpen(true)}
                className={`max-w-[250px] cursor-pointer w-full md:w-auto border rounded-md px-3 py-2 h-[44px] flex items-center justify-center gap-2 bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] text-[14px] relative ${
                  isDark 
                    ? 'dark:border-gray-500 dark:from-slate-800 dark:to-slate-800 dark:text-gray-300' 
                    : 'border-[#E4E7EC] text-[#667085]'
                }`}
              >
                <Filter size={20} className={isDark ? 'text-gray-400' : 'text-[#667085]'} />
                {t('recharge3.filters.advanceFilter')}
                {isAnyFilterApplied && (
                  <span className="absolute top-[-6px] right-[-6px] w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                    <span className="text-white text-[10px]">!</span>
                  </span>
                )}
              </button>
            </div>
          </div>
         
          {isAnyFilterApplied && (
            <button
              onClick={clearAllFilters}
              className="max-w-[150px] cursor-pointer w-full md:w-auto border border-red-500 rounded-md px-3 py-2 h-[44px] flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 transition-colors dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              <X size={16} />
              {t('recharge3.filters.clearAll')}
            </button>
          )}
        </div>

        <div className="flex items-center w-full md:w-auto">
          <button
            onClick={() => setTopupModalOpen(true)}
            className="bg-[#CD0C02] px-4 py-[10px] rounded-md text-white flex items-center gap-2 hover:bg-[#a80a02] transition-colors"
          >
            <Wallet size={20} />
            {t('recharge3.buttons.topUp')}
          </button>
        </div>
      </div>


      <div className="mt-5">
        <ARHCustomDataTable
          data={data?.data || []}
          columns={columns}
          meta={data?.meta || {
            page: filter.page,
            pages: Math.ceil((data?.meta?.totalRecords || 0) / filter.limit) || 1,
            total: data?.meta?.totalRecords || 0,
            limit: filter.limit
          }}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={handleRefetch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          searchQuery={filter.search}
          emptyState={customEmptyState}
          errorState={customErrorState}
          className="border border-gray-200 dark:border-gray-700 rounded-xl"
          rowClassName={`hover:bg-gray-50 transition-colors ${isDark ? 'dark:hover:bg-white/[0.05]' : ''}`}
        />
      </div>

      {isDatePickerOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setIsDatePickerOpen(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl max-w-full overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            <ARHCustomDateFiler
              onApply={handleDateRangeApply} 
              onClose={() => setIsDatePickerOpen(false)} 
            />
          </div>
        </div>
      )}
      
      {isAdvanceFilterOpen && (
        <ARHAdvanceFilterModal
          isOpen={isAdvanceFilterOpen}
          onClose={() => setIsAdvanceFilterOpen(false)}
          onApply={handleApplyAdvanceFilters}
          filterConfig={filterConfig}
          initialValues={initialAdvanceValues}
        />
      )}
    </div>
  );
}

export default Recharge;